import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import API_CONFIG from '../config/api';
import { WearPreset, WearPosePreset, WearCategory, WearGender } from '../types/api';
import {
  PlusIcon,
  PhotoIcon,
  TrashIcon,
  PencilSquareIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const WEAR = API_CONFIG.ENDPOINTS.WEAR_CATALOGUE;

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function getToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: Record<string, unknown>;
  formData?: FormData;
}

async function apiFetch(path: string, options: ApiOptions = {}): Promise<any> {
  const token = await getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const init: RequestInit = { method: options.method || 'GET', headers };

  if (options.formData) {
    init.body = options.formData;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }

  const response = await fetch(`${API_CONFIG.BASE_URL}${path}`, init);
  let data: any = {};
  try {
    data = await response.json();
  } catch {
    // non-JSON error body
  }

  if (!response.ok) {
    const msg = data?.detail || data?.message || `Request failed (HTTP ${response.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return data;
}

async function uploadFile(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('userId', auth.currentUser?.uid || 'default_user');
  fd.append('files', file);
  const data = await apiFetch(WEAR.UPLOAD, { method: 'POST', formData: fd });
  const uploaded = data?.uploaded;
  if (!uploaded || !uploaded.length || !uploaded[0].url) {
    throw new Error('Upload failed - no URL returned');
  }
  return uploaded[0].url;
}

// Try to read full preset list (including inactive) from Firestore. Falls back
// to the public API (active only) when Firestore is unavailable.
async function fetchPresetsFromFirestore(cat: WearCategory): Promise<WearPreset[] | null> {
  try {
    const ref = collection(db, 'wear_catalogue_presets');
    const q = query(ref, where('category', '==', cat));
    const snap = await getDocs(q);

    const list: WearPreset[] = [];
    snap.docs.forEach((doc) => {
      const d: any = doc.data();
      let timestamp: string | undefined;
      if (d.timestamp) {
        timestamp = typeof d.timestamp.toDate === 'function'
          ? d.timestamp.toDate().toISOString()
          : String(d.timestamp);
      }
      list.push({
        modelId: d.modelId || doc.id,
        modelName: d.modelName || 'Untitled',
        gender: d.gender === 'male' ? 'male' : 'female',
        faceImageUrl: d.faceImageUrl || '',
        category: d.category === 'yoga-wear' ? 'yoga-wear' : 'active-wear',
        profilePic: d.profilePic !== undefined ? String(d.profilePic) : undefined,
        active: d.active !== false,
        type: d.type || 'ethnic',
        timestamp,
        poses: Array.isArray(d.poses) ? d.poses.map((p: any) => ({
          poseName: p.poseName || '',
          prompt: p.prompt || '',
          previewImageUrl: p.previewImageUrl || '',
        })) : [],
      });
    });
    return list;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Small shared UI primitives
// ---------------------------------------------------------------------------

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

interface SmartImgProps {
  src?: string;
  alt?: string;
  className?: string;
  fallback?: React.ReactNode;
}

const SmartImg: React.FC<SmartImgProps> = ({ src, alt, className, fallback }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);

  if (!src || broken) {
    return (
      <div className={`${className || ''} flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-400`}>
        {fallback || <PhotoIcon className="h-6 w-6" />}
      </div>
    );
  }
  return <img src={src} alt={alt || ''} onError={() => setBroken(true)} className={className} />;
};

interface ImageFieldProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
  onUpload: (file: File) => Promise<string>;
  placeholder?: string;
  showPreview?: boolean;
}

const ImageField: React.FC<ImageFieldProps> = ({ label, value, onChange, onUpload, placeholder = 'https://', showPreview = true }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const url = await onUpload(file);
      onChange(url);
    } catch (err: any) {
      setUploadError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">{label}</label>
        {uploadError && <span className="text-[11px] text-red-500">{uploadError}</span>}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 min-w-0 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/10 px-3 py-2 text-xs font-semibold text-orange-600 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ArrowUpTrayIcon className="h-4 w-4" />}
          {uploading ? 'Uploading' : 'Upload'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
      {value && showPreview && (
        <div className="mt-2 h-20 w-28 overflow-hidden rounded-lg ring-1 ring-slate-200 dark:ring-white/10">
          <SmartImg src={value} className="h-full w-full object-cover" />
        </div>
      )}
    </div>
  );
};

interface ModelFormState {
  modelName: string;
  gender: WearGender;
  faceImageUrl: string;
  profilePic: string;
  active: boolean;
  type: string;
}

const emptyModelForm = (): ModelFormState => ({
  modelName: '',
  gender: 'female',
  faceImageUrl: '',
  profilePic: '',
  active: true,
  type: 'ethnic',
});

interface ModelFieldsProps {
  form: ModelFormState;
  setForm: (updater: (prev: ModelFormState) => ModelFormState) => void;
  onUpload: (file: File) => Promise<string>;
}

const ModelFields: React.FC<ModelFieldsProps> = ({ form, setForm, onUpload }) => {
  const inputCls =
    'w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/60';

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Model name</label>
        <input
          type="text"
          value={form.modelName}
          onChange={(e) => setForm((prev) => ({ ...prev, modelName: e.target.value }))}
          placeholder="e.g. Aarav"
          className={inputCls}
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Gender</label>
        <div className="flex gap-2">
          {(['female', 'male'] as WearGender[]).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, gender: g }))}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium capitalize transition-colors ${
                form.gender === g
                  ? 'border-orange-400 bg-orange-50 text-orange-700 dark:border-orange-500/50 dark:bg-orange-500/10 dark:text-orange-300'
                  : 'border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Type</label>
        <input
          type="text"
          value={form.type}
          onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
          placeholder="e.g. ethnic"
          className={inputCls}
        />
      </div>

      <ImageField label="Face image URL" value={form.faceImageUrl} onChange={(url) => setForm((prev) => ({ ...prev, faceImageUrl: url }))} onUpload={onUpload} />

      <ImageField label="Profile picture URL (optional)" value={form.profilePic} onChange={(url) => setForm((prev) => ({ ...prev, profilePic: url }))} onUpload={onUpload} />

      <label className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 px-4 py-3">
        <div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Active in catalogue</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Only active presets are shown to users</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={form.active}
          onClick={() => setForm((prev) => ({ ...prev, active: !prev.active }))}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            form.active ? 'bg-orange-500' : 'bg-slate-300 dark:bg-slate-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              form.active ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </label>
    </div>
  );
};

function ModalShell({ title, subtitle, onClose, children }: { title: string; subtitle?: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 backdrop-blur-sm p-4 md:p-6">
      <div className="glass-card w-full max-w-2xl my-6">
        <div className="flex items-start justify-between border-b border-slate-200/70 dark:border-white/10 px-5 py-4">
          <div>
            <h3 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-5 space-y-5 max-h-[70vh] overflow-y-auto scrollbar-thin">{children}</div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel, busy, onConfirm, onCancel }: {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="glass-card w-full max-w-sm p-6 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-white">{title}</h3>
        <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{message}</div>
        <div className="mt-6 flex justify-center gap-3">
          <button
            onClick={onCancel}
            className="rounded-lg border border-slate-200 dark:border-white/10 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Poses management
// ---------------------------------------------------------------------------

interface PoseFormState {
  poseName: string;
  posePrompt: string;
  poseImageUrl: string;
  gender: WearGender;
  type: string;
  active: boolean;
}

function emptyPoseForm(category: WearCategory): PoseFormState {
  return {
    poseName: '',
    posePrompt: '',
    poseImageUrl: '',
    gender: 'female',
    type: category === 'yoga-wear' ? 'yoga' : 'gym',
    active: true,
  };
}

const PoseFormModal: React.FC<{
  category: WearCategory;
  preset?: WearPosePreset;
  onClose: () => void;
  onSaved: () => void;
  onToast: (type: 'success' | 'error', msg: string) => void;
}> = ({ category, preset, onClose, onSaved, onToast }) => {
  const [form, setForm] = useState<PoseFormState>(() =>
    preset
      ? {
          poseName: preset.poseName,
          posePrompt: preset.posePrompt,
          poseImageUrl: preset.poseImageUrl,
          gender: preset.gender,
          type: preset.type || 'gym',
          active: preset.active,
        }
      : emptyPoseForm(category)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputCls =
    'w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/60';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const poseName = form.poseName.trim();
    if (!poseName) {
      setError('Pose name is required');
      return;
    }
    if (!form.posePrompt.trim()) {
      setError('Prompt is required');
      return;
    }
    if (!form.poseImageUrl.trim()) {
      setError('Preview image URL is required');
      return;
    }
    const payload: Record<string, unknown> = {
      poseName,
      posePrompt: form.posePrompt.trim(),
      poseImageUrl: form.poseImageUrl.trim(),
      gender: form.gender,
      category,
      type: form.type.trim() || (category === 'yoga-wear' ? 'yoga' : 'gym'),
      active: form.active,
    };
    setSaving(true);
    setError(null);
    try {
      if (preset) {
        await apiFetch(`${WEAR.ADMIN_POSES}/${preset.poseId}`, { method: 'PUT', body: payload });
        onToast('success', `Pose "${poseName}" updated`);
      } else {
        await apiFetch(WEAR.ADMIN_POSES, { method: 'POST', body: payload });
        onToast('success', `Pose "${poseName}" created`);
      }
      await onSaved();
    } catch (err: any) {
      setError(err?.message || (preset ? 'Failed to update pose' : 'Failed to create pose'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={preset ? `Edit Pose — ${preset.poseName}` : 'New Pose'}
      subtitle={category === 'yoga-wear' ? 'Yoga Wear category' : 'Gym / Active Wear category'}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Pose name</label>
          <input
            type="text"
            value={form.poseName}
            onChange={(e) => setForm((prev) => ({ ...prev, poseName: e.target.value }))}
            placeholder="e.g. full body, side pose"
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Prompt</label>
          <textarea
            value={form.posePrompt}
            onChange={(e) => setForm((prev) => ({ ...prev, posePrompt: e.target.value }))}
            placeholder="Text prompt describing the pose"
            rows={3}
            className={inputCls}
          />
        </div>
        <ImageField label="Preview image URL" value={form.poseImageUrl} onChange={(url) => setForm((prev) => ({ ...prev, poseImageUrl: url }))} onUpload={uploadFile} />

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Gender</label>
          <div className="flex gap-2">
            {(['female', 'male'] as WearGender[]).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, gender: g }))}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium capitalize transition-colors ${
                  form.gender === g
                    ? 'border-orange-400 bg-orange-50 text-orange-700 dark:border-orange-500/50 dark:bg-orange-500/10 dark:text-orange-300'
                    : 'border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Type</label>
          <div className="flex gap-2">
            {['gym', 'yoga'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, type: t }))}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium capitalize transition-colors ${
                  form.type === t
                    ? 'border-orange-400 bg-orange-50 text-orange-700 dark:border-orange-500/50 dark:bg-orange-500/10 dark:text-orange-300'
                    : 'border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <label className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Active</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Only active poses are shown to users</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.active}
            onClick={() => setForm((prev) => ({ ...prev, active: !prev.active }))}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              form.active ? 'bg-orange-500' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                form.active ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </label>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex items-center justify-end gap-3 border-t border-slate-200/70 dark:border-white/10 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 dark:border-white/10 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-solid inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {saving ? 'Saving...' : preset ? 'Save Changes' : 'Create Pose'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

const PosesSection: React.FC<{
  category: WearCategory;
  createOpen: boolean;
  onRequestCreate: () => void;
  onCreateClose: () => void;
  onToast: (type: 'success' | 'error', msg: string) => void;
}> = ({ category, createOpen, onRequestCreate, onCreateClose, onToast }) => {
  const [poses, setPoses] = useState<WearPosePreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingPose, setEditingPose] = useState<WearPosePreset | null>(null);
  const [deletingPose, setDeletingPose] = useState<WearPosePreset | null>(null);
  const [typeFilter, setTypeFilter] = useState('all');

  const loadPoses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchCat = async (cat: WearCategory): Promise<WearPosePreset[]> => {
        const data = await apiFetch(`${WEAR.ADMIN_POSES}?category=${cat}`);
        return Array.isArray(data?.poses) ? data.poses : [];
      };
      const [act, yog] = await Promise.all([fetchCat('active-wear'), fetchCat('yoga-wear')]);
      const byId = new Map<string, WearPosePreset>();
      [...act, ...yog].forEach((p) => byId.set(p.poseId, p));
      setPoses(Array.from(byId.values()).sort((a, b) => a.poseName.localeCompare(b.poseName)));
    } catch (err: any) {
      setError(err?.message || 'Failed to load poses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPoses();
  }, [loadPoses]);

  const handleToggleActive = async (pose: WearPosePreset) => {
    try {
      setBusy(true);
      await apiFetch(`${WEAR.ADMIN_POSES}/${pose.poseId}`, {
        method: 'PUT',
        body: { active: !pose.active },
      });
      onToast('success', `Pose "${pose.poseName}" is now ${!pose.active ? 'active' : 'inactive'}`);
      await loadPoses();
    } catch (err: any) {
      onToast('error', err?.message || 'Failed to update pose');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPose) return;
    try {
      setBusy(true);
      await apiFetch(`${WEAR.ADMIN_POSES}/${deletingPose.poseId}`, { method: 'DELETE' });
      onToast('success', `Pose "${deletingPose.poseName}" deleted`);
      setDeletingPose(null);
      await loadPoses();
    } catch (err: any) {
      onToast('error', err?.message || 'Failed to delete pose');
    } finally {
      setBusy(false);
    }
  };

  const poseTypes = Array.from(new Set(poses.map((p) => p.type).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b));
  const visiblePoses = typeFilter === 'all' ? poses : poses.filter((p) => p.type === typeFilter);
  const activeCount = visiblePoses.filter((p) => p.active).length;

  return (
    <>
      {error && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {/* Type filter */}
      <div className="mb-5 flex items-center gap-2">
        <label htmlFor="wear-type-filter" className="text-sm font-semibold text-slate-600 dark:text-slate-300">
          Type
        </label>
        <select
          id="wear-type-filter"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
        >
          <option value="all">All</option>
          {poseTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <div className="relative mx-auto mb-5 h-16 w-16">
              <div className="absolute inset-0 rounded-full border-4 border-orange-200 dark:border-orange-900/40"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-orange-500 animate-spin"></div>
              <div className="absolute inset-3 rounded-full bg-orange-400 opacity-20"></div>
            </div>
            <p className="text-slate-700 dark:text-slate-300 font-medium">Loading poses...</p>
          </div>
        </div>
      ) : visiblePoses.length === 0 ? (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800">
              <PhotoIcon className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">No poses yet</h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Add your first pose for this category to get started.</p>
            <button
              onClick={onRequestCreate}
              className="mt-5 btn-solid px-4 py-2 text-sm font-semibold text-white rounded-xl inline-flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              New Pose
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-5 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            {visiblePoses.length} poses ({activeCount} active)
          </div>
          <div className="flex flex-col gap-2">
            {visiblePoses.map((pose) => (
            <div key={pose.poseId} className="glass-card flex items-center gap-3 px-3 py-2">
              <SmartImg src={pose.poseImageUrl} alt={pose.poseName} className="h-12 w-12 shrink-0 rounded-lg bg-slate-100 dark:bg-slate-800 object-contain" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-sm font-semibold text-slate-800 dark:text-white">{pose.poseName}</h3>
                  {pose.type && (
                    <span className="shrink-0 rounded bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-white/10 px-1.5 py-0.5 text-[10px] font-bold capitalize text-slate-600 dark:text-slate-300">
                      {pose.type}
                    </span>
                  )}
                  {!pose.active && (
                    <span className="shrink-0 rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">Inactive</span>
                  )}
                </div>
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                  <span className="capitalize">{pose.gender}</span> · {pose.posePrompt || 'No prompt'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  onClick={() => handleToggleActive(pose)}
                  disabled={busy}
                  title={pose.active ? 'Deactivate' : 'Activate'}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors ${
                    pose.active
                      ? 'border-emerald-200 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10'
                      : 'border-slate-200 text-slate-500 hover:bg-slate-100 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${pose.active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                  {pose.active ? 'Active' : 'Inactive'}
                </button>

                <button
                  onClick={() => setEditingPose(pose)}
                  className="rounded-md p-1.5 text-slate-500 hover:bg-orange-50 hover:text-orange-600 dark:text-slate-400 dark:hover:bg-orange-500/10 dark:hover:text-orange-400 transition-colors"
                  title="Edit pose"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDeletingPose(pose)}
                  className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-colors"
                  title="Delete pose"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          </div>
        </>
      )}

      {createOpen && (
        <PoseFormModal
          category={category}
          onClose={onCreateClose}
          onSaved={async () => {
            onCreateClose();
            await loadPoses();
          }}
          onToast={onToast}
        />
      )}

      {editingPose && (
        <PoseFormModal
          category={category}
          preset={editingPose}
          onClose={() => setEditingPose(null)}
          onSaved={async () => {
            setEditingPose(null);
            await loadPoses();
          }}
          onToast={onToast}
        />
      )}

      {deletingPose && (
        <ConfirmModal
          title="Delete pose?"
          message={<>This will permanently delete <b>{deletingPose.poseName}</b>.</>}
          confirmLabel="Delete"
          busy={busy}
          onConfirm={handleDelete}
          onCancel={() => setDeletingPose(null)}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type Section = 'models' | 'poses';

const WearCatalogue: React.FC = () => {
  const [section, setSection] = useState<Section>('models');
  const [typeFilter, setTypeFilter] = useState('all');
  const [presets, setPresets] = useState<WearPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [poseCreateOpen, setPoseCreateOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<WearPreset | null>(null);
  const [deletingModel, setDeletingModel] = useState<WearPreset | null>(null);
  const [busy, setBusy] = useState(false);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const category: WearCategory = typeFilter === 'yoga' ? 'yoga-wear' : 'active-wear';

  const showToast = useCallback((type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const loadPresets = useCallback(async () => {
    setLoading(true);
    setError(null);

    const fetchCategory = async (cat: WearCategory): Promise<WearPreset[]> => {
      try {
        const data = await apiFetch(cat === 'yoga-wear' ? WEAR.YOGA_MODEL_PRESETS : WEAR.ACTIVE_MODEL_PRESETS);
        return Array.isArray(data?.models) ? data.models : [];
      } catch (err: any) {
        setError(err?.message || 'Failed to load presets');
        return [];
      }
    };

    // Reliable primary source: the documented public model-presets APIs.
    // Both categories are fetched once and filtered on the frontend.
    const lists = await Promise.all([fetchCategory('active-wear'), fetchCategory('yoga-wear')]);
    const byId = new Map<string, WearPreset>();
    lists.flat().forEach((p) => byId.set(p.modelId, p));
    setPresets(Array.from(byId.values()).sort((a, b) => a.modelName.localeCompare(b.modelName)));
    setLoading(false);

    // Supplemental: Firestore list (also includes inactive models). Never blocks
    // the UI - if it times out or is denied, the API results stay. Only adds
    // models the API did not return, so API fields (e.g. `type`) are preserved.
    try {
      const [act, yog] = await Promise.all([
        withTimeout(fetchPresetsFromFirestore('active-wear'), 3000),
        withTimeout(fetchPresetsFromFirestore('yoga-wear'), 3000),
      ]);
      setPresets((prev) => {
        const merged = new Map(prev.map((p) => [p.modelId, p]));
        [act, yog].forEach((all) => {
          if (all === null) return;
          all.forEach((p) => {
            if (!merged.has(p.modelId)) merged.set(p.modelId, p);
          });
        });
        return Array.from(merged.values()).sort((a, b) => a.modelName.localeCompare(b.modelName));
      });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (section === 'models') loadPresets();
  }, [loadPresets, section]);

  const handleToggleActive = async (preset: WearPreset) => {
    try {
      setBusy(true);
      await apiFetch(`${WEAR.ADMIN_MODELS}/${preset.modelId}`, {
        method: 'PUT',
        body: { active: !preset.active },
      });
      showToast('success', `"${preset.modelName}" is now ${!preset.active ? 'active' : 'inactive'}`);
      await loadPresets();
    } catch (err: any) {
      showToast('error', err?.message || 'Failed to update model');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingModel) return;
    try {
      setBusy(true);
      await apiFetch(`${WEAR.ADMIN_MODELS}/${deletingModel.modelId}`, { method: 'DELETE' });
      showToast('success', `"${deletingModel.modelName}" deleted`);
      setDeletingModel(null);
      await loadPresets();
    } catch (err: any) {
      showToast('error', err?.message || 'Failed to delete model');
    } finally {
      setBusy(false);
    }
  };

  const modelTypes = Array.from(new Set(presets.map((p) => p.type).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b));
  const visiblePresets = typeFilter === 'all' ? presets : presets.filter((p) => p.type === typeFilter);
  const activeCount = visiblePresets.filter((p) => p.active).length;

  return (
    <div className="flex-1 flex flex-col h-full relative z-10">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-[60] animate-[fadeUp_0.25s_ease-out]">
          <div
            className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium shadow-xl ${
              toast.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/80 dark:text-emerald-300 dark:border-emerald-500/30'
                : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/80 dark:text-red-300 dark:border-red-500/30'
            }`}
          >
            {toast.type === 'success' ? <CheckCircleIcon className="h-5 w-5 shrink-0" /> : <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />}
            {toast.msg}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="flex-shrink-0 border-b border-slate-200/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 backdrop-blur-xl px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Wear Catalogue</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Manage AI fashion models & poses for gym and yoga catalogues</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Models / Poses toggle */}
            <div className="flex items-center rounded-xl bg-slate-100/80 dark:bg-white/5 border border-slate-200/70 dark:border-white/10 p-1 backdrop-blur">
              {(['models', 'poses'] as Section[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setSection(s)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200 ${
                    section === s
                      ? 'bg-orange-600 text-white shadow-md shadow-orange-600/30'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-white/10'
                  }`}
                >
                  {s === 'models' ? 'Models' : 'Poses'}
                </button>
              ))}
            </div>

            {section === 'models' ? (
              <button
                onClick={() => setShowCreate(true)}
                className="btn-solid px-4 py-2 text-sm font-semibold text-white rounded-xl inline-flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                New Model
              </button>
            ) : (
              <button
                onClick={() => setPoseCreateOpen(true)}
                className="btn-solid px-4 py-2 text-sm font-semibold text-white rounded-xl inline-flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                New Pose
              </button>
            )}

            <button
              onClick={section === 'models' ? loadPresets : undefined}
              disabled={loading && section === 'models'}
              title="Refresh"
              className="rounded-lg border border-slate-200/70 dark:border-white/10 bg-white dark:bg-slate-800 p-2 text-slate-500 hover:text-orange-600 dark:text-slate-400 dark:hover:text-orange-400 disabled:opacity-50 transition-colors"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading && section === 'models' ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto scrollbar-thin p-6">
        {section === 'poses' ? (
          <PosesSection
            category={category}
            createOpen={poseCreateOpen}
            onRequestCreate={() => setPoseCreateOpen(true)}
            onCreateClose={() => setPoseCreateOpen(false)}
            onToast={showToast}
          />
        ) : (
          <>
            {error && !loading && (
              <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                {error}
              </div>
            )}

            {/* Type filter */}
            <div className="mb-5 flex items-center gap-2">
              <label htmlFor="wear-type-filter" className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                Type
              </label>
              <select
                id="wear-type-filter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
              >
                <option value="all">All</option>
                {modelTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <div className="relative mx-auto mb-5 h-16 w-16">
                    <div className="absolute inset-0 rounded-full border-4 border-orange-200 dark:border-orange-900/40"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-orange-500 animate-spin"></div>
                    <div className="absolute inset-3 rounded-full bg-orange-400 opacity-20"></div>
                  </div>
                  <p className="text-slate-700 dark:text-slate-300 font-medium">Loading catalogue...</p>
                </div>
              </div>
            ) : visiblePresets.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800">
                    <PhotoIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">No models yet</h3>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Add your first model to get started.</p>
                  <button
                    onClick={() => setShowCreate(true)}
                    className="mt-5 btn-solid px-4 py-2 text-sm font-semibold text-white rounded-xl inline-flex items-center gap-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    New Model
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-5 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  {visiblePresets.length} models ({activeCount} active)
                </div>
        <div className="flex flex-col gap-2">
          {visiblePresets.map((preset) => (
            <div key={preset.modelId} className="glass-card flex items-center gap-3 px-3 py-2">
              <SmartImg src={preset.faceImageUrl} alt={preset.modelName} className="h-12 w-12 shrink-0 rounded-lg bg-slate-100 dark:bg-slate-800 object-contain" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-sm font-semibold text-slate-800 dark:text-white">{preset.modelName}</h3>
                  {!preset.active && (
                    <span className="shrink-0 rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">Inactive</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
                  {preset.gender} · {preset.type || 'ethnic'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  onClick={() => handleToggleActive(preset)}
                  disabled={busy}
                  title={preset.active ? 'Deactivate' : 'Activate'}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors ${
                    preset.active
                      ? 'border-emerald-200 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10'
                      : 'border-slate-200 text-slate-500 hover:bg-slate-100 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${preset.active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                  {preset.active ? 'Active' : 'Inactive'}
                </button>

                <button
                  onClick={() => setEditingModel(preset)}
                  className="rounded-md p-1.5 text-slate-500 hover:bg-orange-50 hover:text-orange-600 dark:text-slate-400 dark:hover:bg-orange-500/10 dark:hover:text-orange-400 transition-colors"
                  title="Edit model"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDeletingModel(preset)}
                  className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-colors"
                  title="Delete model"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
              </>
            )}
          </>
        )}
      </main>

      {/* Create model modal */}
      {showCreate && (
        <CreateModelModal
          category={category}
          onClose={() => setShowCreate(false)}
          onSaved={async () => {
            setShowCreate(false);
            await loadPresets();
          }}
          onToast={showToast}
        />
      )}

      {/* Edit model modal */}
      {editingModel && (
        <EditModelModal
          preset={editingModel}
          onClose={() => setEditingModel(null)}
          onSaved={async () => {
            setEditingModel(null);
            await loadPresets();
          }}
          onToast={showToast}
        />
      )}

      {/* Delete model confirm */}
      {deletingModel && (
        <ConfirmModal
          title="Delete model?"
          message={<>This will permanently delete <b>{deletingModel.modelName}</b>.</>}
          confirmLabel="Delete"
          busy={busy}
          onConfirm={handleDelete}
          onCancel={() => setDeletingModel(null)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create model modal
// ---------------------------------------------------------------------------

const CreateModelModal: React.FC<{
  category: WearCategory;
  onClose: () => void;
  onSaved: () => void;
  onToast: (type: 'success' | 'error', msg: string) => void;
}> = ({ category, onClose, onSaved, onToast }) => {
  const [form, setForm] = useState<ModelFormState>(emptyModelForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const modelName = form.modelName.trim();
    if (!modelName) {
      setError('Model name is required');
      return;
    }
    if (!form.faceImageUrl.trim()) {
      setError('Face image URL is required');
      return;
    }
    const payload: Record<string, unknown> = {
      modelName,
      gender: form.gender,
      faceImageUrl: form.faceImageUrl.trim(),
      category,
      profilePic: form.profilePic.trim() || undefined,
      active: form.active,
      type: form.type.trim() || 'ethnic',
    };
    setSaving(true);
    setError(null);
    try {
      await apiFetch(WEAR.ADMIN_MODELS, { method: 'POST', body: payload });
      onToast('success', `Model "${modelName}" created`);
      await onSaved();
    } catch (err: any) {
      setError(err?.message || 'Failed to create model');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="New Model" subtitle={`Adding to ${category === 'yoga-wear' ? 'yoga-wear' : 'gym / active-wear'} catalogue`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <ModelFields form={form} setForm={setForm} onUpload={uploadFile} />

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex items-center justify-end gap-3 border-t border-slate-200/70 dark:border-white/10 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 dark:border-white/10 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-solid inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {saving ? 'Creating...' : 'Create Model'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// Edit model modal
// ---------------------------------------------------------------------------

const EditModelModal: React.FC<{
  preset: WearPreset;
  onClose: () => void;
  onSaved: () => void;
  onToast: (type: 'success' | 'error', msg: string) => void;
}> = ({ preset, onClose, onSaved, onToast }) => {
  const [form, setForm] = useState<ModelFormState>(() => ({
    modelName: preset.modelName,
    gender: preset.gender,
    faceImageUrl: preset.faceImageUrl || '',
    profilePic: preset.profilePic || '',
    active: preset.active,
    type: preset.type || 'ethnic',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm({
      modelName: preset.modelName,
      gender: preset.gender,
      faceImageUrl: preset.faceImageUrl || '',
      profilePic: preset.profilePic || '',
      active: preset.active,
      type: preset.type || 'ethnic',
    });
  }, [preset]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const modelName = form.modelName.trim();
    if (!modelName) {
      setError('Model name is required');
      return;
    }
    if (!form.faceImageUrl.trim()) {
      setError('Face image URL is required');
      return;
    }
    const payload: Record<string, unknown> = {
      modelName,
      gender: form.gender,
      faceImageUrl: form.faceImageUrl.trim(),
      profilePic: form.profilePic.trim() || undefined,
      active: form.active,
      type: form.type.trim() || 'ethnic',
    };
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`${WEAR.ADMIN_MODELS}/${preset.modelId}`, { method: 'PUT', body: payload });
      onToast('success', `Model "${modelName}" updated`);
      await onSaved();
    } catch (err: any) {
      setError(err?.message || 'Failed to update model');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={`Edit Model — ${preset.modelName}`} subtitle={`${preset.category === 'yoga-wear' ? 'Yoga Wear' : 'Gym / Active Wear'} · ID ${preset.modelId}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <ModelFields form={form} setForm={setForm} onUpload={uploadFile} />

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex items-center justify-end gap-3 border-t border-slate-200/70 dark:border-white/10 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 dark:border-white/10 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-solid inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

export default WearCatalogue;